import { useEffect, useMemo, useState } from "react";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { useAIEngineStore, DEFAULT_AI_ENGINE_SETTINGS, normalizeAIEngineSettings } from "../stores/aiEngineStore";

const MODULE_PROFILE_ROWS = [
  { key: "tools/ai", label: "AI Analysis", group: "tools" },
  { key: "tools/ncs", label: "NCs", group: "tools" },
  { key: "tools/photo", label: "Photo", group: "tools" },
  { key: "tools/assistente", label: "Assistente", group: "tools" },
  { key: "tools/changes", label: "Change Management", group: "tools" },
  { key: "tools/trails", label: "Trails", group: "tools" },
  { key: "tools/meetings", label: "Meeting", group: "tools" },
  { key: "audit/preparation", label: "Preparation", group: "audit" },
  { key: "audit/checklist", label: "Checklist", group: "audit" },
  { key: "audit/report", label: "Report", group: "audit" },
  { key: "audit/program", label: "Program", group: "audit" },
  { key: "audit/followup", label: "Follow-up", group: "audit" },
];
const MODULE_GROUP_TABS = [
  { key: "global", label: "Global" },
  { key: "tools", label: "Ferramentas" },
  { key: "audit", label: "Auditoria" },
];

const CRITICAL_TOOL_KEYS = ["tools/ai", "tools/ncs", "tools/photo", "tools/assistente", "tools/changes", "tools/trails"];
const AUDIT_KEYS = ["audit/preparation", "audit/checklist", "audit/report", "audit/program", "audit/followup"];
const COST_MODEL_INDEX = {
  opus: 8,
  sonnet: 3,
  haiku: 1,
};
const COST_MODULE_WEIGHT = {
  "tools/ai": 1.25,
  "tools/ncs": 1.2,
  "tools/photo": 1.1,
  "tools/assistente": 1.1,
  "tools/changes": 1,
  "tools/trails": 1.3,
  "tools/meetings": 0.75,
  "audit/preparation": 0.9,
  "audit/checklist": 1.05,
  "audit/report": 1.1,
  "audit/program": 0.7,
  "audit/followup": 0.65,
};
const COST_CONTEXT_FACTOR = { focused: 0.85, balanced: 1, broad: 1.15, full: 1.35, inherit: 1 };
const COST_DEEPTHINK_FACTOR = { low: 0.9, balanced: 1, high: 1.22, inherit: 1 };
const COST_QUALITY_FACTOR = { fast: 0.9, balanced: 1, strict: 1.12, inherit: 1 };
const MODEL_OPTIONS = [
  { value: "claude-haiku-4-5-20251001", label: "Rapido (Haiku 4.5)" },
  { value: "claude-sonnet-4-6", label: "Equilibrado (Sonnet 4.6)" },
  { value: "claude-opus-4-7", label: "Profundo (Opus 4.7)" },
];
const TOKEN_SLIDER_MIN = 2048;
const TOKEN_SLIDER_MAX = 16384;
const TOKEN_SLIDER_STEP = 1024;
const DIRECTION_TEMPLATES = [
  { key: "objetivo", label: "Mais objetivo", text: "Priorize objetividade, respostas curtas e conclusao direta." },
  { key: "conservador", label: "Mais conservador", text: "Adote postura conservadora, sem extrapolar alem da evidencia objetiva." },
  { key: "explicativo", label: "Mais explicativo", text: "Explique com didatica os porques, conectando evidencias e requisitos." },
  { key: "custo", label: "Foco em custo", text: "Minimize tamanho da resposta e evite etapas que aumentem custo sem ganho relevante." },
  { key: "precisao", label: "Foco em precisao", text: "Priorize rigor maximo, verificacao interna e rastreabilidade normativa completa." },
];

function modelCostIndex(modelName = "") {
  const m = String(modelName || "").toLowerCase();
  if (m.includes("opus")) return COST_MODEL_INDEX.opus;
  if (m.includes("haiku")) return COST_MODEL_INDEX.haiku;
  return COST_MODEL_INDEX.sonnet;
}

function clampToken(v) {
  const n = Number(v || 0);
  if (!n || Number.isNaN(n)) return TOKEN_SLIDER_MIN;
  return Math.max(TOKEN_SLIDER_MIN, Math.min(TOKEN_SLIDER_MAX, n));
}

function tokenCostTier(value) {
  if (value <= 4096) return "baixo";
  if (value <= 10240) return "medio";
  return "alto";
}

function presetLabel(preset) {
  if (preset === "economy") return "Economia";
  if (preset === "precision") return "Maxima Precisao";
  return "Balanceado";
}

function FieldLabel({ label, tip }) {
  return (
    <div className="mb-1.5 flex items-center gap-1.5">
      <Label className="block">{label}</Label>
      {tip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-border text-[10px] font-mono text-muted-foreground hover:bg-secondary"
              aria-label={`Ajuda sobre ${label}`}
            >
              ?
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {tip}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

export function AIEngineConfigPanel() {
  const settings = useAIEngineStore(s => s.settings);
  const setSettings = useAIEngineStore(s => s.setSettings);
  const resetSettings = useAIEngineStore(s => s.resetSettings);
  const [draft, setDraft] = useState(normalizeAIEngineSettings(settings || DEFAULT_AI_ENGINE_SETTINGS));
  const [customModelEditing, setCustomModelEditing] = useState(false);
  const [panelMode, setPanelMode] = useState("basic");
  const [moduleTab, setModuleTab] = useState("global");
  const [sessionLocked, setSessionLocked] = useState(false);
  const [applyNotice, setApplyNotice] = useState("");

  useEffect(() => {
    setDraft(normalizeAIEngineSettings(settings || DEFAULT_AI_ENGINE_SETTINGS));
  }, [settings]);

  useEffect(() => {
    if (customModelEditing && MODEL_OPTIONS.some((m) => m.value === draft.models.precision)) {
      setCustomModelEditing(false);
    }
  }, [customModelEditing, draft.models.precision]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(settings), [draft, settings]);

  const setField = (path, value) => {
    if (sessionLocked) return;
    const next = JSON.parse(JSON.stringify(draft));
    const keys = path.split(".");
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
    cur[keys[keys.length - 1]] = value;
    setDraft(next);
  };

  const setToggle = (path) => {
    if (sessionLocked) return;
    const next = JSON.parse(JSON.stringify(draft));
    const keys = path.split(".");
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
    const leaf = keys[keys.length - 1];
    cur[leaf] = !cur[leaf];
    setDraft(next);
  };

  const getModuleProfile = (key) => draft.moduleProfiles?.[key] || DEFAULT_AI_ENGINE_SETTINGS.moduleProfiles[key];
  const moduleRowsForTab = useMemo(() => {
    if (moduleTab === "global") return MODULE_PROFILE_ROWS;
    return MODULE_PROFILE_ROWS.filter((row) => row.group === moduleTab);
  }, [moduleTab]);

  const setModuleProfileField = (key, path, value) => {
    if (sessionLocked) return;
    const next = JSON.parse(JSON.stringify(draft));
    if (!next.moduleProfiles) next.moduleProfiles = {};
    if (!next.moduleProfiles[key]) next.moduleProfiles[key] = JSON.parse(JSON.stringify(DEFAULT_AI_ENGINE_SETTINGS.moduleProfiles[key] || DEFAULT_AI_ENGINE_SETTINGS.moduleProfiles["tools/ai"]));
    const keys = path.split(".");
    let cur = next.moduleProfiles[key];
    for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
    cur[keys[keys.length - 1]] = value;
    setDraft(next);
  };

  const toggleModuleProfile = (key) => {
    if (sessionLocked) return;
    const p = getModuleProfile(key);
    setModuleProfileField(key, "enabled", !p.enabled);
  };

  const buildDraftForPreset = (preset, sourceDraft) => {
    const next = normalizeAIEngineSettings(sourceDraft);
    const allModuleKeys = MODULE_PROFILE_ROWS.map(m => m.key);

    if (preset === "economy") {
      next.profile = "economy";
      next.deepthink = "low";
      next.contextMode = "focused";
      next.qualityMode = "fast";
      next.breadthMode = "narrow";
      next.maxTokens = { ...next.maxTokens, precision: 6144, trails: 6144, fast: 2048 };
      next.models = { ...next.models, precision: "claude-sonnet-4-6", pro: "claude-opus-4-7", fast: "claude-haiku-4-5-20251001" };
      next.toggles = { ...next.toggles, retryJson: true, escalation: true, validation: true, schema: true };

      allModuleKeys.forEach((k) => {
        next.moduleProfiles[k] = {
          ...next.moduleProfiles[k],
          enabled: false,
          contextMode: "inherit",
          deepthink: "inherit",
          qualityMode: "inherit",
          breadthMode: "inherit",
          maxTokens: { ...next.moduleProfiles[k].maxTokens, precision: 0, trails: 0, fast: 0 },
        };
      });
      CRITICAL_TOOL_KEYS.forEach((k) => {
        next.moduleProfiles[k] = {
          ...next.moduleProfiles[k],
          enabled: true,
          contextMode: "balanced",
          deepthink: "balanced",
          qualityMode: "balanced",
          breadthMode: "standard",
          maxTokens: { ...next.moduleProfiles[k].maxTokens, precision: 8192, trails: 8192, fast: 0 },
        };
      });
    }

    if (preset === "balanced") {
      const base = normalizeAIEngineSettings(DEFAULT_AI_ENGINE_SETTINGS);
      base.directivePrompt = next.directivePrompt || "";
      return base;
    }

    if (preset === "precision") {
      next.profile = "strict";
      next.deepthink = "high";
      next.contextMode = "broad";
      next.qualityMode = "strict";
      next.breadthMode = "wide";
      next.maxTokens = { ...next.maxTokens, precision: 16384, trails: 16384, fast: 8192 };
      next.models = { ...next.models, precision: "claude-opus-4-7", pro: "claude-opus-4-7", fast: "claude-sonnet-4-6" };
      next.toggles = { ...next.toggles, retryJson: true, escalation: true, validation: true, schema: true };

      allModuleKeys.forEach((k) => {
        next.moduleProfiles[k] = {
          ...next.moduleProfiles[k],
          enabled: true,
          contextMode: "full",
          deepthink: "high",
          qualityMode: "strict",
          breadthMode: "wide",
          maxTokens: { ...next.moduleProfiles[k].maxTokens, precision: 16384, trails: 16384, fast: 8192 },
        };
      });

      next.moduleProfiles["tools/meetings"] = {
        ...next.moduleProfiles["tools/meetings"],
        enabled: true,
        contextMode: "broad",
        deepthink: "balanced",
        qualityMode: "balanced",
        breadthMode: "standard",
        maxTokens: { ...next.moduleProfiles["tools/meetings"].maxTokens, precision: 8192, trails: 0, fast: 4096 },
      };

      AUDIT_KEYS.forEach((k) => {
        next.moduleProfiles[k] = {
          ...next.moduleProfiles[k],
          enabled: true,
          contextMode: "full",
          deepthink: "high",
          qualityMode: "strict",
          breadthMode: "wide",
          maxTokens: { ...next.moduleProfiles[k].maxTokens, precision: 12288, trails: 0, fast: 0 },
        };
      });
    }

    return normalizeAIEngineSettings(next);
  };

  const applyPreset = (preset) => {
    if (sessionLocked) return;
    setDraft(buildDraftForPreset(preset, draft));
  };

  const applyPresetToCurrentTab = (preset) => {
    if (sessionLocked) return;
    if (moduleTab !== "tools" && moduleTab !== "audit") return;
    const source = normalizeAIEngineSettings(draft);
    const presetDraft = buildDraftForPreset(preset, source);
    const targetKeys = MODULE_PROFILE_ROWS
      .filter((row) => row.group === moduleTab)
      .map((row) => row.key);
    targetKeys.forEach((key) => {
      source.moduleProfiles[key] = {
        ...source.moduleProfiles[key],
        ...presetDraft.moduleProfiles[key],
        models: {
          ...source.moduleProfiles[key]?.models,
          ...presetDraft.moduleProfiles[key]?.models,
        },
        maxTokens: {
          ...source.moduleProfiles[key]?.maxTokens,
          ...presetDraft.moduleProfiles[key]?.maxTokens,
        },
      };
    });
    setDraft(normalizeAIEngineSettings(source));
  };

  const estimateCostIndex = (cfg) => {
    const normalized = normalizeAIEngineSettings(cfg);
    const moduleProfiles = normalized.moduleProfiles || {};
    let total = 0;
    MODULE_PROFILE_ROWS.forEach((row) => {
      const globalTokens = Number(normalized.maxTokens?.precision || 0);
      const globalModel = normalized.models?.precision || "";
      const globalContext = normalized.contextMode || "balanced";
      const globalDeepthink = normalized.deepthink || "balanced";
      const globalQuality = normalized.qualityMode || "balanced";
      const profile = moduleProfiles[row.key] || {};
      const tokens = profile.enabled && Number(profile.maxTokens?.precision || 0) > 0
        ? Number(profile.maxTokens.precision)
        : globalTokens;
      const model = profile.enabled && profile.models?.precision
        ? profile.models.precision
        : globalModel;
      const context = profile.enabled && profile.contextMode && profile.contextMode !== "inherit"
        ? profile.contextMode
        : globalContext;
      const deepthink = profile.enabled && profile.deepthink && profile.deepthink !== "inherit"
        ? profile.deepthink
        : globalDeepthink;
      const quality = profile.enabled && profile.qualityMode && profile.qualityMode !== "inherit"
        ? profile.qualityMode
        : globalQuality;
      const weight = COST_MODULE_WEIGHT[row.key] || 1;
      total += (tokens / 1000) * modelCostIndex(model) * (COST_CONTEXT_FACTOR[context] || 1) * (COST_DEEPTHINK_FACTOR[deepthink] || 1) * (COST_QUALITY_FACTOR[quality] || 1) * weight;
    });
    return total;
  };

  const costPreview = useMemo(() => {
    const current = estimateCostIndex(draft);
    const economy = estimateCostIndex(buildDraftForPreset("economy", draft));
    const balanced = estimateCostIndex(buildDraftForPreset("balanced", draft));
    const precision = estimateCostIndex(buildDraftForPreset("precision", draft));
    return { current, economy, balanced, precision };
  }, [draft]);

  const presetCards = [
    {
      key: "economy",
      subtitle: "Menor custo operacional",
      details: "Mantem rigor essencial, reduz tokens e foco em agilidade.",
      cost: costPreview.economy,
      tone: "border-emerald-300 bg-emerald-100 text-emerald-900",
    },
    {
      key: "balanced",
      subtitle: "Padrao recomendado",
      details: "Equilibrio entre profundidade, velocidade e confiabilidade.",
      cost: costPreview.balanced,
      tone: "border-amber-300 bg-amber-100 text-amber-900",
    },
    {
      key: "precision",
      subtitle: "Rigor maximo",
      details: "Mais verificacoes, contexto amplo e maior consumo.",
      cost: costPreview.precision,
      tone: "border-rose-300 bg-rose-100 text-rose-900",
    },
  ];

  const customModelEnabled = !MODEL_OPTIONS.some((m) => m.value === draft.models.precision);
  const showCustomModelInput = customModelEditing || customModelEnabled;
  const saveDraft = (applyNow = false) => {
    if (sessionLocked) return;
    setSettings(normalizeAIEngineSettings(draft));
    if (applyNow) {
      const time = new Date().toLocaleTimeString();
      setApplyNotice(`Configuracao aplicada agora as ${time}.`);
    } else {
      setApplyNotice("");
    }
  };

  return (
    <TooltipProvider>
      <div className="mt-6 rounded-lg border border-border bg-card p-4">
      <div className="mb-3">
        <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-primary mb-1">
          pilotagem ia · runtime config
        </div>
        <h3 className="text-base font-semibold text-foreground">Configuracao da Engine IA (multi-ferramenta)</h3>
        <p className="text-[12px] text-muted-foreground mt-1">
          Ajuste rapido com presets e controle fino por dropdown, slider e direcionamento textual.
        </p>
      </div>
      <Tabs value={panelMode} onValueChange={setPanelMode} className="mb-3">
        <TabsList className="w-auto">
          <TabsTrigger value="basic">Basico</TabsTrigger>
          <TabsTrigger value="advanced">Avancado</TabsTrigger>
        </TabsList>
      </Tabs>
      {applyNotice && (
        <div className="mb-3 rounded border border-emerald-400/40 bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-200">
          {applyNotice}
        </div>
      )}

      <div className="rounded-md border border-border p-3 mb-3 bg-secondary/30">
        <div className="font-mono text-[9px] tracking-[0.18em] uppercase text-muted-foreground mb-2">
          presets de operacao (1 clique)
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {presetCards.map((card) => (
            <button
              key={card.key}
              onClick={() => applyPreset(card.key)}
              className={`rounded border p-3 text-left transition-all hover:shadow-sm ${card.tone}`}
            >
              <div className="text-[12px] font-semibold">{presetLabel(card.key)}</div>
              <div className="text-[11px] opacity-90">{card.subtitle}</div>
              <div className="text-[10px] opacity-75 mt-1">{card.details}</div>
              <div className="mt-2 text-[11px] font-mono">indice custo: {card.cost.toFixed(1)}</div>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">
          Custo atual estimado: <strong className="text-foreground">{costPreview.current.toFixed(1)}</strong> (indice relativo, nao e cobranca oficial da API).
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <FieldLabel label="Estrategia global" tip="Define o equilibrio geral entre custo, velocidade e rigor das respostas." />
          <Select value={draft.profile} onValueChange={(v) => setField("profile", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="economy">Economia</SelectItem>
              <SelectItem value="balanced">Balanceado</SelectItem>
              <SelectItem value="strict">Maxima Precisao</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel label="Profundidade de raciocinio" tip="Quanto mais alto, mais verificacao interna e maior custo/tempo por resposta." />
          <Select value={draft.deepthink} onValueChange={(v) => setField("deepthink", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Baixo</SelectItem>
              <SelectItem value="balanced">Medio</SelectItem>
              <SelectItem value="high">Alto</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <div>
          <FieldLabel label="Contexto" tip="Controla quanto material e clausulas entram no raciocinio da IA." />
          <Select value={draft.contextMode} onValueChange={(v) => setField("contextMode", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="focused">Essencial</SelectItem>
              <SelectItem value="balanced">Balanceado</SelectItem>
              <SelectItem value="broad">Amplo</SelectItem>
              <SelectItem value="full">Completo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel label="Qualidade" tip="Niveis mais altos fazem revisao mais rigorosa antes da resposta final." />
          <Select value={draft.qualityMode} onValueChange={(v) => setField("qualityMode", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fast">Rascunho rapido</SelectItem>
              <SelectItem value="balanced">Profissional</SelectItem>
              <SelectItem value="strict">Maxima revisao</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel label="Abrangencia" tip="Ajusta o escopo de exploracao: foco estreito ou mais amplo." />
          <Select value={draft.breadthMode} onValueChange={(v) => setField("breadthMode", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="narrow">Foco estreito</SelectItem>
              <SelectItem value="standard">Padrao</SelectItem>
              <SelectItem value="wide">Expandido</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <FieldLabel label="Modelo principal" tip="Modelo usado na maior parte das respostas da engine." />
          <Select
            value={showCustomModelInput ? "custom" : draft.models.precision}
            onValueChange={(v) => {
              if (v === "custom") {
                setCustomModelEditing(true);
                return;
              }
              setCustomModelEditing(false);
              setField("models.precision", v);
            }}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {MODEL_OPTIONS.map((model) => (
                <SelectItem key={model.value} value={model.value}>{model.label}</SelectItem>
              ))}
              <SelectItem value="custom">Customizado</SelectItem>
            </SelectContent>
          </Select>
          {showCustomModelInput && (
            <Input className="mt-2" value={draft.models.precision} onChange={(e) => setField("models.precision", e.target.value)} />
          )}
        </div>
        <div>
          <FieldLabel label="Fallback rapido (reparo JSON)" tip="Modelo de apoio quando precisa corrigir estrutura de resposta rapidamente." />
          <Select value={draft.models.fast} onValueChange={(v) => setField("models.fast", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {MODEL_OPTIONS.map((model) => (
                <SelectItem key={model.value} value={model.value}>{model.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <div>
          <FieldLabel label={`Tokens principais: ${draft.maxTokens.precision} (${tokenCostTier(draft.maxTokens.precision)})`} tip="Limite de tamanho para respostas principais. Maior valor aumenta detalhe e custo." />
          <input
            type="range"
            min={TOKEN_SLIDER_MIN}
            max={TOKEN_SLIDER_MAX}
            step={TOKEN_SLIDER_STEP}
            value={clampToken(draft.maxTokens.precision)}
            onChange={(e) => setField("maxTokens.precision", clampToken(e.target.value))}
            className="w-full accent-primary"
          />
        </div>
        <div>
          <FieldLabel label={`Tokens trilhas: ${draft.maxTokens.trails} (${tokenCostTier(draft.maxTokens.trails)})`} tip="Limite de tokens para fluxos de trilha/encadeamento mais longos." />
          <input
            type="range"
            min={TOKEN_SLIDER_MIN}
            max={TOKEN_SLIDER_MAX}
            step={TOKEN_SLIDER_STEP}
            value={clampToken(draft.maxTokens.trails)}
            onChange={(e) => setField("maxTokens.trails", clampToken(e.target.value))}
            className="w-full accent-primary"
          />
        </div>
        <div>
          <FieldLabel label={`Tokens rapido: ${draft.maxTokens.fast} (${tokenCostTier(draft.maxTokens.fast)})`} tip="Limite do modo rapido/fallback. Menor valor reduz custo e latencia." />
          <input
            type="range"
            min={TOKEN_SLIDER_MIN}
            max={12288}
            step={TOKEN_SLIDER_STEP}
            value={clampToken(draft.maxTokens.fast)}
            onChange={(e) => setField("maxTokens.fast", clampToken(e.target.value))}
            className="w-full accent-primary"
          />
        </div>
      </div>

      <div className="rounded-md border border-border p-3 mb-3">
        <FieldLabel label="Direcionamento da IA (prompt gestor)" tip="Instrucao textual que a IA passa a considerar como estilo e prioridade em todas as respostas." />
        <Textarea
          value={draft.directivePrompt || ""}
          onChange={(e) => setField("directivePrompt", e.target.value)}
          placeholder="Ex.: Priorize objetividade, evidencia forte e trilha de verificacao. Evite respostas longas."
          className="min-h-[88px]"
        />
        <div className="flex flex-wrap gap-2 mt-2">
          {DIRECTION_TEMPLATES.map((tpl) => (
            <button
              key={tpl.key}
              onClick={() => setField("directivePrompt", `${String(draft.directivePrompt || "").trim()} ${tpl.text}`.trim())}
              className="rounded-sm border border-border px-2 py-1 text-[10px] font-mono uppercase text-muted-foreground hover:bg-secondary"
            >
              {tpl.label}
            </button>
          ))}
        </div>
      </div>

      {panelMode === "advanced" && (
        <>
          <Tabs value={moduleTab} onValueChange={setModuleTab} className="mb-3">
            <TabsList className="w-auto">
              {MODULE_GROUP_TABS.map((tab) => (
                <TabsTrigger key={tab.key} value={tab.key}>{tab.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {moduleTab === "global" && (
            <div className="rounded-md border border-border p-3 mb-3">
              <div className="font-mono text-[9px] tracking-[0.18em] uppercase text-muted-foreground mb-2">
                governanca global da engine
              </div>
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-[11px] text-muted-foreground">
                  Trave a sessão durante operação sensível para evitar alterações acidentais.
                </p>
                <button
                  onClick={() => setSessionLocked(v => !v)}
                  className={`px-2 py-1 rounded-sm border text-[10px] font-mono uppercase ${
                    sessionLocked
                      ? "border-rose-300 bg-rose-100 text-rose-900"
                      : "border-emerald-300 bg-emerald-100 text-emerald-900"
                  }`}
                >
                  {sessionLocked ? "sessao travada" : "travar sessao"}
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground mb-2">
                Estes controles valem para toda a plataforma, exceto quando uma ferramenta estiver com override ativo.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <button onClick={() => setToggle("enabled")} className={`px-2 py-2 rounded-sm border text-[10px] font-mono uppercase ${draft.enabled ? "border-primary/60 bg-primary/15 text-primary" : "border-border bg-secondary text-muted-foreground"}`}>engine {draft.enabled ? "on" : "off"}</button>
                <button onClick={() => setToggle("toggles.retryJson")} className={`px-2 py-2 rounded-sm border text-[10px] font-mono uppercase ${draft.toggles.retryJson ? "border-primary/60 bg-primary/15 text-primary" : "border-border bg-secondary text-muted-foreground"}`}>retry json</button>
                <button onClick={() => setToggle("toggles.escalation")} className={`px-2 py-2 rounded-sm border text-[10px] font-mono uppercase ${draft.toggles.escalation ? "border-primary/60 bg-primary/15 text-primary" : "border-border bg-secondary text-muted-foreground"}`}>escalation</button>
                <button onClick={() => setToggle("toggles.validation")} className={`px-2 py-2 rounded-sm border text-[10px] font-mono uppercase ${draft.toggles.validation ? "border-primary/60 bg-primary/15 text-primary" : "border-border bg-secondary text-muted-foreground"}`}>validation</button>
                <button onClick={() => setToggle("toggles.schema")} className={`px-2 py-2 rounded-sm border text-[10px] font-mono uppercase ${draft.toggles.schema ? "border-primary/60 bg-primary/15 text-primary" : "border-border bg-secondary text-muted-foreground"}`}>schema</button>
              </div>
            </div>
          )}

          {(moduleTab === "tools" || moduleTab === "audit") && (
            <div className="rounded-md border border-border p-3 mb-3">
              <div className="font-mono text-[9px] tracking-[0.18em] uppercase text-muted-foreground mb-2">
                ajustes por modulo · {moduleTab === "tools" ? "ferramentas" : "auditoria"}
              </div>
              <div className="mb-2 flex flex-wrap gap-2">
                <button
                  onClick={() => applyPresetToCurrentTab("economy")}
                  className="rounded-sm border border-emerald-300 bg-emerald-100 px-2 py-1 text-[10px] font-mono uppercase text-emerald-900"
                >
                  aplicar economia nesta aba
                </button>
                <button
                  onClick={() => applyPresetToCurrentTab("balanced")}
                  className="rounded-sm border border-amber-300 bg-amber-100 px-2 py-1 text-[10px] font-mono uppercase text-amber-900"
                >
                  aplicar balanceado nesta aba
                </button>
                <button
                  onClick={() => applyPresetToCurrentTab("precision")}
                  className="rounded-sm border border-rose-300 bg-rose-100 px-2 py-1 text-[10px] font-mono uppercase text-rose-900"
                >
                  aplicar precisao nesta aba
                </button>
              </div>
              <div className="space-y-2 mt-3">
                {moduleRowsForTab.map((row) => {
                  const p = getModuleProfile(row.key);
                  return (
                    <div key={row.key} className="rounded border border-border/70 p-2">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div>
                          <div className="text-[12px] font-semibold text-foreground">{row.label}</div>
                          <div className="text-[10px] font-mono text-muted-foreground">{row.key}</div>
                        </div>
                        <button
                          onClick={() => toggleModuleProfile(row.key)}
                          className={`px-2 py-1 rounded-sm border text-[10px] font-mono uppercase ${p.enabled ? "border-primary/60 bg-primary/15 text-primary" : "border-border bg-secondary text-muted-foreground"}`}
                        >
                          override {p.enabled ? "on" : "off"}
                        </button>
                      </div>
                      {!p.enabled && (
                        <div className="mb-2 inline-flex rounded-sm border border-zinc-300 bg-zinc-100 px-2 py-0.5 text-[10px] font-mono text-zinc-800">
                          herdando global
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                        <div>
                          <Label className="mb-1 block text-[10px]">Contexto</Label>
                          <Select value={p.contextMode || "inherit"} onValueChange={(v) => setModuleProfileField(row.key, "contextMode", v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="inherit">Herdar</SelectItem>
                              <SelectItem value="focused">Essencial</SelectItem>
                              <SelectItem value="balanced">Balanceado</SelectItem>
                              <SelectItem value="broad">Amplo</SelectItem>
                              <SelectItem value="full">Completo</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="mb-1 block text-[10px]">Profundidade</Label>
                          <Select value={p.deepthink || "inherit"} onValueChange={(v) => setModuleProfileField(row.key, "deepthink", v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="inherit">Herdar</SelectItem>
                              <SelectItem value="low">Baixo</SelectItem>
                              <SelectItem value="balanced">Medio</SelectItem>
                              <SelectItem value="high">Alto</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="mb-1 block text-[10px]">Qualidade</Label>
                          <Select value={p.qualityMode || "inherit"} onValueChange={(v) => setModuleProfileField(row.key, "qualityMode", v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="inherit">Herdar</SelectItem>
                              <SelectItem value="fast">Rapido</SelectItem>
                              <SelectItem value="balanced">Profissional</SelectItem>
                              <SelectItem value="strict">Maxima revisao</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="mb-1 block text-[10px]">Tokens (0 = herdar): {p.maxTokens?.precision ?? 0}</Label>
                          <input
                            type="range"
                            min={0}
                            max={TOKEN_SLIDER_MAX}
                            step={TOKEN_SLIDER_STEP}
                            value={Number(p.maxTokens?.precision ?? 0)}
                            onChange={(e) => setModuleProfileField(row.key, "maxTokens.precision", Number(e.target.value || 0))}
                            className="w-full accent-primary"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      <div className="flex gap-2">
        <Button size="sm" onClick={() => saveDraft(false)} disabled={!dirty || sessionLocked}>salvar engine</Button>
        <Button size="sm" onClick={() => saveDraft(true)} disabled={!dirty || sessionLocked}>salvar e aplicar agora</Button>
        <Button size="sm" variant="outline" onClick={() => setDraft(normalizeAIEngineSettings(settings))} disabled={sessionLocked}>descartar</Button>
        <Button size="sm" variant="outline" onClick={() => { if (sessionLocked) return; resetSettings(); setDraft(normalizeAIEngineSettings(DEFAULT_AI_ENGINE_SETTINGS)); }} disabled={sessionLocked}>reset padrão</Button>
      </div>
      </div>
    </TooltipProvider>
  );
}

